import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { Customer } from '../interfaces/customer';
import { HttpService } from '../../shared/services/http.service';
import { ToastrService } from 'ngx-toastr';
import { AccountingGroup } from '../../admin/modules/accounting-group/interfaces/accounting-group';
import { Insurer } from '../../insurer/interfaces/insurer';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ShippingOrder } from '../../shipping/interfaces/shipping-order';
import { PaginatingResponse } from '../../shared/interfaces/paginating.response';
import { PrivateCustomer } from '../../private-customer/interface/private-customer';
import { Invoice } from '../../billing/interfaces/invoice';

export interface CustomerResponse extends PaginatingResponse {
  data: Customer[];
}

@Injectable()
export class CustomerService {

  /**
   * Ein `BehaviourSubject` mit dem derzeit geladenen Kunden-Datensätzen als Array
   *
   * @var BehaviorSubject<Customer[]>
   */
  private customerData = new BehaviorSubject<Customer[]>(null);

  private accountingGroups = new BehaviorSubject<AccountingGroup[]>(null);

  private insurers = new BehaviorSubject<Insurer[]>(null);

  private shippingHistory = new BehaviorSubject<ShippingOrder[]>(null);

  private invoiceHistory = new BehaviorSubject<Invoice[]>(null);

  data: CustomerResponse;

  private _page: number;

  private _limit: number = 50;

  private _sort: { field: string, direction: 'asc' | 'desc' };

  public set sort(sort: any) {
    this._sort = sort;
  }

  private _filters: any = {};

  public set filters(filters: any) {
    this._filters = filters;
  }

  set page(value: number) {
    this._page = value;
  }

  set limit(value: number) {
    this._limit = value;
  }

  /**
   * Konstruktor - Initialisiert die `HttpService` und `ToastrService` Klassen-Member.
   *
   * @param http
   * @param toast
   */
  constructor(
    private http: HttpService, private toast: ToastrService
  ) {}

  /**
   * Holt die Kundenliste und setzt sie in der Klasse
   */
  public getCustomers = (): Observable<Customer[]> => {
    return this.customerData.asObservable();
  }

  /**
   * Schickt die Anfrage an den Server um die Kunden zu aktualisieren.
   * Liest vorher die aktuellen Filter aus und sendet sie als Query-Parameter mit.
   */
  public requestCustomers() {
    let queryParams = new HttpParams();
    if (this._limit) {
      queryParams = queryParams.set('limit', String(this._limit));
    }
    if (this._page) {
      queryParams = queryParams.set('page', String(this._page));
    }

    if (this._filters) {
      Object.keys(this._filters).map((key) => {
        if (this._filters[key]) {
          queryParams = queryParams.set(key, this._filters[key]);
        }
      });
    }

    if (this._sort) {
      queryParams = queryParams.set('sort', this._sort.field).set('sortDir', this._sort.direction);
    }

    this.http.get('/api/customer/customers', this.setCustomerData, null, queryParams);
  }

  /**
   * Setzt die Kundendaten im Service
   *
   * @param response any
   */
  private setCustomerData = async (response: any) => {

    const customerArray = Object.keys(response.data).map(function (index) {
      const customer = response.data[index] as Customer;
      return customer;
    });

    this.data = response;
    this.data.data = customerArray;
    this.customerData.next(customerArray);
  }

  /**
   * Sendet eine Anfrage an den Server um die möglichen Kostenträger zu erhalten
   *
   * qparam callBack
   */
  public requestAccountingGroups = () => {
    this.http.get('/api/accounting-group/list', this.setAccountingGroups);
  }

  /**
   * Funktion zum Setzen der vom Server empfangenen Kostenträger als BehaviourSUbject
   *
   * @param response
   */
  private setAccountingGroups = (response: AccountingGroup[]) => {
    this.accountingGroups.next(response);
  }

  /**
   * Gibt die Kostenträger als Observable zurück
   *
   * @returns Observable
   */
  public getAccountingroups = () => {
    return this.accountingGroups.asObservable();
  }

  /**
   * Sendet eine Anfrage an den Server um die möglichen Pflegekassen zu erhalten
   *
   */
  public requestInsurers = () => {
    this.http.get('/api/insurer/list', this.setInsurers);
  }

  /**
   * Funktion zum Setzen der Pflegeklassen als BehaviourSubject
   *
   * @param response
   */
  private setInsurers = (response: Insurer[]) => {
    this.insurers.next(response);
  }

  /**
   * Gibt die Pflegekassen als Observable zurück
   *
   * @returns Observable
   */
  public getInsurers = () => {
    return this.insurers.asObservable();
  }

  /**
   * Fügt einen neuen Customer-Datensatz hinzu
   * Gibt nichts zurück oder Formfehler, wenn welche zurückgegeben werden
   *
   * @param payload
   * @param callBack
   */
  public async addNewCustomer(payload: any) {
    const route = '/api/customer/add';
    const response = await lastValueFrom(this.http.postWithoutCallback(route, payload))
      .catch((e: HttpErrorResponse) => {
        return e;
      });
    if (response instanceof HttpErrorResponse) {
      return response.error;
    }
    this.toast.success('Erfolgreich gespeichert');
    this.requestCustomers();
    return;
  }

  /**
   * Funktion zum Updaten der Kunden-Stammdaten
   * Gibt nichts zurück oder Formfehler, wenn welche zurückgegeben werden
   *
   * @param customer
   * @param payload
   */
  public async updateCustomer(customer, payload: any) {
    const route = '/api/customer/update/' + customer.Id;
    const response = await lastValueFrom(this.http.postWithoutCallback(route, payload))
      .catch((e: HttpErrorResponse) => {
        return e;
      });

    if (response instanceof HttpErrorResponse) {
      return response.error;
    }
    this.toast.success('Erfolgreich gespeichert');
    this.requestCustomers();
    return;
  }

  /**
   * Funktion zum verarbeiten der Serverantwort
   *
   * @param response
   */
  public handleServerResponse = (response: any) => {
    this.toast.success(response.message);
    this.requestCustomers();
  }

  /**
   * Sendet eine Anfrage an den Server um den Vertrag zu beenden
   */
  public submitCancelContract = (customer: Customer, date: Date) => {
    const route = '/api/customer/cancel-contract/' + customer.Id;
    this.http.post(route, {date: date}, this.handleServerResponse);
  }

  /**
   * Verschickt die Anfrage, um den Kunden auf "verstorben" zu setzen an den Server
   *
   * @param customer
   * @param date
   */
  public setDeceased = async (customer: Customer, date: string) => {
    const route = '/api/customer/set-deceased/' + customer.Id;
    const response = await lastValueFrom(this.http.postWithoutCallback(route, { Date: date }));

    if (!(response instanceof HttpErrorResponse)) {
      this.handleServerResponse(response);
    } else if (response.error.message) {
      this.toast.error(response.error.message);
    }
  }

  /**
   * Funktion für das permanente Löschen eines Kundendatensatzes
   *
   * @param customer
   */
  public hardDelete = (customer: Customer) => {
    const route = '/api/customer/' + customer.Id;
    this.http.delete(route, this.handleDelete);
  }

  /**
   * @param response
   */
  public handleDelete = (response: any) => {
    this.toast.success(response.message);
    this.requestCustomers();
  }

  /**
   * Funktion zum Laden eines Benutzers aus der Datenbank. Hier kann man immer
   * mit einer erfolgreichen Response rechnen, da der Server im Erfolgsfall
   * (ID Gefunden) die Benutzerdaten zurückgibt. Sollte der Benutzer nicht
   * vorhanden sein, so wird ein leeres Array zurückgegeben. Sollte der Benutzer
   * gelöscht sein, so werden die Benutzerdaten zurückgegeben, aber nicht in der
   * View angezeigt.
   *
   * @param idCustomer
   */
  public async loadOneCustomer(idCustomer: number, customerType: number) {
    const route = '/api/customer/one/' + idCustomer + '/' + customerType;
    const response = await lastValueFrom(this.http.getWithoutCallback(route))
      .catch((e: HttpErrorResponse) => {
        this.toast.error(e.error.message ? e.error.message : 'Fehler beim Laden');
        return;
      });
    if (response) {
      this.replaceCustomer(response);
    }
  }

  /**
   * Funktion zum Ersetzen eines neugeladenen Kundendatensatzes. Dabei ist zu
   * beachten, dass nur Datensätze von Kunden, welche bereits in der Liste
   * vorhanden sind, ersetzt werden.
   *
   * @param customer
   */
  private replaceCustomer(customer: Customer) {
    const currentCustomer = this.customerData.value.find((c: Customer) => {
      return (c.Id === customer.Id);
    });

    if (currentCustomer) {
      const tempData = this.customerData.value;
      const index = tempData.indexOf(currentCustomer);
      tempData[index] = customer;
      this.customerData.next(tempData);
    }
  }

  public async findOneCustomer(idCustomer: number, customerType: number) {
    const route = '/api/customer/one/' + idCustomer + '/' + customerType;
    const response = await lastValueFrom(this.http.getWithoutCallback(route))
      .catch((e: HttpErrorResponse) => {
        this.toast.error(e.error.message ? e.error.message : 'Fehler beim Laden');
        return;
      });

    return response;
  }

  public async searchAutocomplete(searchTerm: string, customerType: number = 1): Promise<Customer[]> {
    const route = `/api/customer/autocomplete/${customerType}`;
    const params = new HttpParams().set('search', searchTerm);
    return await lastValueFrom(this.http.getWithoutCallback(route, params)) as Customer[];
  }

  public getShippingHistory() {
    return this.shippingHistory.asObservable();
  }

  async requestShippingHistory(customer: Customer|PrivateCustomer) {
    const id = customer.Id;
    const customerType = customer.Type;

    const url = '/api/customer/' + id + '/' + customerType + '/shipping-orders';
    const response = await lastValueFrom(this.http.getWithoutCallback(url))
      .catch((e: HttpErrorResponse) => {
        return e;
      });
    if (response instanceof HttpErrorResponse) {
      this.toast.error(response.error.message ? response.error.message : 'Fehler beim Laden der Lieferhistorie');
      return;
    }
    this.shippingHistory.next(response);
  }

  public getInvoiceHistory() {
    return this.invoiceHistory.asObservable();
  }

  async requestInvoiceHistory(customer: Customer|PrivateCustomer) {
    const url = '/api/customer/' + customer.Id + '/' + customer.Type + '/invoices';
    const response = await lastValueFrom(this.http.getWithoutCallback(url))
      .catch((e: HttpErrorResponse) => {
        return e;
      });
    if (response instanceof HttpErrorResponse) {
      this.toast.error(response.error.message ? response.error.message : 'Fehler beim Laden der Rechnungshistorie');
      return;
    }
    this.invoiceHistory.next(response);
  }

  /**
   * Fügt einen filter zum Service hinzu
   *
   * @param key
   * @param value
   */
  addFilter(key: string, value?: any) {
    if (typeof value === "boolean") {
      value = (value) ? '1' : '0';
    }

    if (value) {
      this._filters[key] = value;
    } else if (key === 'expiring') {
        this._filters[key] = false;
    }
  }

  /**
   * Entfernt einen Filter anhand des übergebenen Keys.
   *
   * @param key
   */
  clearFilter(key: string) {
    delete this._filters[key];
  }

}
